import { useCallback } from 'react';
import reactHotToast from 'react-hot-toast';
import { supabase } from '../../../lib/supabase';
import { AlfaDocsService } from '../../../lib/services/alfadocsService';
import { useGoogleCalendar } from '../../../hooks/deals/useGoogleCalendar';
import { useMicrosoftCalendar } from '../../../hooks/deals/useMicrosoftCalendar';
import { resolveUserSyncSettings } from './syncSettings';
import { syncGoogleEvent } from './googleSync';
import { syncMicrosoftEvent } from './microsoftSync';
import { syncAlfaDocsAppointment, resolveActivityAlfaConfig } from './useAlfaDocsSync';
import { buildFinalPatch, resolveSafeType, resolveAlfaDocsSubType, buildActivityAttendees, resolveGoogleOrganizer, demoteFalseSuccess } from './activityPayload';
import type { ActivitySaveInput, ActivitySyncContext, EntityAdapter, SavedActivity } from './types';

/** Ri-esportato: il test verbatim del brief importa `buildFinalPatch` da qui. */
export { buildFinalPatch };

/**
 * Orchestratore di salvataggio dell'attività condivisa (tab "Attività" del drawer).
 * Porta `LeadStatusUpdateDrawer.tsx:1069-1461` (insert/update + AlfaDocs + trigger
 * della sync), ESCLUSA la costruzione dell'evento/la chiamata alle API calendario
 * (già estratte in `eventData.ts`/`googleSync.ts`/`microsoftSync.ts`) e la
 * costruzione del payload/attendee/organizer (estratta in `activityPayload.ts`
 * per il budget di righe del task).
 *
 * Fuori scope (di competenza della SHELL, Task 12): validazione/commit delle
 * modifiche all'entità (`adapter.validateEntityChanges`/`commitEntityChanges`),
 * salvataggio della nota (`adapter.saveNote`), trigger di workflow automation
 * lead-specifici (`workflowExecutionService.onLeadActivityCreated`/
 * `onClientActivityCreated` nella fonte, :1415-1461) — quest'ultimo è
 * intenzionalmente lasciato all'implementazione concreta di
 * `adapter.afterActivitySaved`, dato che dipende da concetti lead-only
 * (pipeline_id, stato lead/cliente) non presenti nel contratto `EntityAdapter`.
 */
export function useActivitySave(adapter: EntityAdapter) {
  const googleApi = useGoogleCalendar();
  const microsoftApi = useMicrosoftCalendar();

  const saveActivity = useCallback(
    async (input: ActivitySaveInput): Promise<SavedActivity | null> => {
      try {
        const { data: userData } = await supabase.auth.getUser();
        const currentUserId = userData.user?.id || null;
        const currentUserEmail = userData.user?.email || null;

        const safeType = await resolveSafeType(input.activityData.type);
        // Sottotipo AlfaDocs (Task 16): derivato dal tipo RAW PRIMA della traduzione
        // canonica di `resolveSafeType` (che per `alfadocs_*` ritorna sempre
        // 'appuntamento' e non lo porterebbe più). `null` per qualunque altro tipo.
        const alfaSubType = resolveAlfaDocsSubType(input.activityData.type);
        const { startDateTime, endDateTime } = input.activityData;
        const isInsert = !input.editingActivity;
        // Regola "Fissare gli appuntamenti": la persona va scelta, nessun ripiego silenzioso
        if (input.requireAssignee && !input.activityData.assigned_to) {
          input.toast.error("Scegli nel calendario di chi va l'appuntamento");
          return null;
        }
        const finalAssignedTo = input.activityData.assigned_to || adapter.defaultAssigneeId || currentUserId;

        // IMPORTANT 3 (review post-Task 8): la combinazione data+ora (incluso il
        // rollover di mezzanotte) è responsabilità della SHELL — qui si valida solo
        // che il risultato sia una data valida, PRIMA di qualunque scrittura.
        if (isNaN(startDateTime.getTime()) || (endDateTime && isNaN(endDateTime.getTime()))) {
          input.toast.error('Data attività non valida');
          return null;
        }

        // === Insert o update della riga sales_activities ===
        let activityRow: any;
        if (isInsert) {
          const payload = {
            title: input.activityData.title,
            description: input.activityData.description || '',
            type: safeType,
            status: input.activityData.status,
            due_date: startDateTime.toISOString(),
            end_date: endDateTime ? endDateTime.toISOString() : null,
            ...adapter.linkageFields(),
            metadata: { include_description_in_event: input.includeDescriptionInEvent },
            ...(input.activityData.status === 'completed' && input.activityData.outcome?.trim()
              ? { outcome: input.activityData.outcome.trim(), completed_at: new Date().toISOString() }
              : {}),
            user_id: currentUserId,
            assigned_to: finalAssignedTo,
            organization_id: input.organizationId,
          };
          const { data, error } = await supabase.from('sales_activities').insert(payload).select().single();
          if (error) throw error;
          activityRow = data;
        } else {
          const updatePayload: Record<string, any> = {
            title: input.activityData.title,
            description: input.activityData.description || '',
            type: safeType,
            status: input.activityData.status,
            due_date: startDateTime.toISOString(),
            end_date: endDateTime ? endDateTime.toISOString() : null,
            assigned_to: finalAssignedTo,
          };
          if (input.activityData.status === 'completed' && input.editingActivity!.status !== 'completed') {
            updatePayload.completed_at = new Date().toISOString();
          }
          // Esito: solo per una task completata e solo se il form lo gestisce (undefined =
          // chiamante che non conosce il campo: la colonna non si tocca).
          if (input.activityData.status === 'completed' && input.activityData.outcome !== undefined) {
            updatePayload.outcome = input.activityData.outcome.trim() || null;
          }
          // Persisti la scelta della spunta descrizione SOLO quando è nota (reidratata
          // o toccata): per le righe storiche con stato sconosciuto non inventiamo un
          // false che al giro dopo diventerebbe "rimozione esplicita" delle note.
          if (input.includeDescriptionKnown !== false) {
            updatePayload.metadata = {
              ...input.editingActivity?.metadata,
              include_description_in_event: input.includeDescriptionInEvent,
            };
          }
          const { data, error } = await supabase
            .from('sales_activities')
            .update(updatePayload)
            .eq('id', input.editingActivity!.id)
            .select()
            .single();
          if (error) throw error;
          activityRow = data;
        }

        // === AlfaDocs (best-effort, mai blocca il resto del salvataggio) ===
        if (isInsert) {
          await syncAlfaDocsAppointment({
            clientId: adapter.clientId as string,
            activityId: activityRow.id,
            alfaType: alfaSubType,
            title: input.activityData.title,
            startDateTime,
            endDateTime,
            organizationId: input.organizationId,
            toast: reactHotToast,
          });
        } else {
          await syncAlfaDocsUpdate(input, adapter.clientId, startDateTime, endDateTime);
        }

        // === Contesto di sync condiviso (decisione #1: ref PERSISTITI, mai stato async) ===
        const existing = input.editingActivity
          ? {
              googleEventId: input.editingActivity.google_event_id ?? null,
              calendarId: input.editingActivity.calendar_id ?? null,
              microsoftEventId: input.editingActivity.microsoft_event_id ?? null,
              assignedTo: input.editingActivity.assigned_to ?? null,
              meetLink: input.editingActivity.meet_link ?? null,
            }
          : null;

        const settings = await resolveUserSyncSettings(finalAssignedTo);
        const selectedEmails = input.selectedAttendees.map((a) => a.email);
        const { updateAttendees, insertGoogleAttendees, insertMicrosoftAttendees } = await buildActivityAttendees({
          safeType,
          selectedEmails,
          currentUserEmail,
          finalAssignedTo,
          invitedCommercial: input.invitedCommercial,
          isInsert,
        });

        const googleOrganizer = isInsert
          ? await resolveGoogleOrganizer({
              safeType,
              finalAssignedTo,
              currentUserId,
              meetOrganizer: input.meetOrganizer,
              invitedCommercial: input.invitedCommercial,
              googleSettings: settings.google,
            })
          : null;

        const baseCtx: Omit<ActivitySyncContext, 'attendees' | 'organizer'> = {
          activityId: activityRow.id,
          title: input.activityData.title,
          descriptionHtml: input.activityData.description || '',
          includeDescriptionInEvent: input.includeDescriptionInEvent,
          includeDescriptionKnown: input.includeDescriptionKnown,
          type: safeType,
          startDateTime,
          endDateTime,
          assigneeId: finalAssignedTo || currentUserId || '',
          currentUserId: currentUserId || '',
          currentUserEmail,
          settings,
          syncOptIn: input.syncOptIn,
          meetingProvider: input.meetingProvider,
          existing,
          clientName: input.clientName ?? null,
          clientPhone: input.clientPhone ?? null,
          clientEmail: input.clientEmail ?? null,
          entityUrl: adapter.entityUrl(),
        };

        const googleCtx: ActivitySyncContext = {
          ...baseCtx,
          attendees: isInsert ? insertGoogleAttendees : updateAttendees,
          organizer: isInsert ? googleOrganizer : null,
        };
        const microsoftCtx: ActivitySyncContext = {
          ...baseCtx,
          attendees: isInsert ? insertMicrosoftAttendees : updateAttendees,
          organizer: null,
        };

        // === Sync sequenziali: Google poi Microsoft (come fonte) ===
        // ECCEZIONE (fix round 1 del redesign drawer): quando l'assegnatario NON ha più
        // la sync Google ma ha quella Microsoft e l'attività porta un `google_event_id`
        // persistito, la cancellazione dell'evento Google (fix #3 di googleSync) è di
        // fatto la prima metà di un MOVE verso Outlook. In quel caso si inverte l'ordine
        // — prima Microsoft, poi Google — e la delete viene autorizzata SOLO se la
        // scrittura Microsoft è andata davvero a buon fine (`ok` dopo la demozione del
        // falso successo). Se Microsoft fallisce, l'evento Google resta dov'è: meglio un
        // evento sul calendario sbagliato che nessun evento da nessuna parte.
        const isMoveToMicrosoft =
          !settings.google.enabled && !!existing?.googleEventId && settings.microsoft.enabled;

        let googleResultRaw;
        let microsoftResultRaw;
        if (isMoveToMicrosoft) {
          microsoftResultRaw = await syncMicrosoftEvent(microsoftCtx, microsoftApi);
          const microsoftOk = demoteFalseSuccess(microsoftResultRaw).status === 'ok';
          googleResultRaw = await syncGoogleEvent({ ...googleCtx, allowStaleCleanup: microsoftOk }, googleApi);
        } else {
          googleResultRaw = await syncGoogleEvent(googleCtx, googleApi);
          microsoftResultRaw = await syncMicrosoftEvent(microsoftCtx, microsoftApi);
        }

        // CRITICAL (review post-Task 8): 'ok' con eventId null è un falso successo,
        // SEMPRE (insert E update — vedi docstring di `demoteFalseSuccess` in
        // activityPayload.ts) → trattato come 'error' ai fini di toast/patch.
        const google = demoteFalseSuccess(googleResultRaw);
        const microsoft = demoteFalseSuccess(microsoftResultRaw);

        // === UN unico update finale ===
        const patch = buildFinalPatch(google, microsoft);
        if (Object.keys(patch).length > 0) {
          const { error: patchError } = await supabase.from('sales_activities').update(patch).eq('id', activityRow.id);
          if (!patchError) {
            activityRow = { ...activityRow, ...patch };
          } else {
            console.error('[useActivitySave] patch finale fallito:', patchError);
          }
        }

        // === Toast di esito (fonte :1633-1637 Google, :1354/:1677 Microsoft) ===
        const actionLabel = isInsert ? 'creata' : 'aggiornata';
        if (google.status === 'auth_error') {
          input.toast.error(`Attività ${actionLabel}, ma i permessi di Google Calendar sono insufficienti. Vai al profilo per riconnettere l'account con permessi completi.`);
        } else if (google.status === 'error') {
          input.toast.error(`Attività ${actionLabel}, ma errore nella sincronizzazione con Google Calendar`);
        }
        if (google.inviteNotSent) {
          input.toast.error(`Appuntamento salvato in agenda, ma Google non ha inviato l'invito email al lead: l'account ha raggiunto il limite giornaliero di inviti. Invia il link a mano.`);
        }
        if (microsoft.status === 'auth_error' || microsoft.status === 'error') {
          input.toast.error(`Attività ${actionLabel}, ma errore nella sincronizzazione con il calendario Microsoft`);
        }
        // IMPORTANT 4 (review post-Task 8): fonte :1625-1627 — toast dedicato quando
        // l'insert genera un Meet link (SOLO insert: in update il Meet non viene mai
        // rigenerato, vedi googleSync.ts).
        if (isInsert && google.meetLink) {
          input.toast.success(`Meet creato! Link: ${google.meetLink}`);
        }
        // Fonte: il toast di successo è incondizionato, anche quando la sync ha già
        // mostrato un errore sopra (:1358-1359, :1681-1682) — l'attività è comunque salvata.
        input.toast.success(`Attività ${actionLabel} con successo`);

        try {
          await adapter.afterActivitySaved(activityRow as SavedActivity, safeType, { isInsert });
        } catch (afterError) {
          console.error('[useActivitySave] afterActivitySaved failed:', afterError);
        }

        return activityRow as SavedActivity;
      } catch (error) {
        console.error('[useActivitySave] saveActivity failed:', error);
        input.toast.error("Errore durante il salvataggio dell'attività");
        return null;
      }
    },
    [adapter, googleApi, microsoftApi]
  );

  return { saveActivity };
}

/**
 * AlfaDocs ramo UPDATE (fonte :1128-1168) — instrada allo studio dell'APPUNTAMENTO
 * (metadata), fallback primaria cliente, fallback connessione più vecchia.
 * Best-effort: qualunque errore resta un `console.warn`, mai un toast/blocco
 * (fedele alla fonte, che non mostra toast di errore su questo ramo).
 *
 * Gating SOLO su `metadata.alfadocs_appointment_id` (mai sul `type` dell'attività): il
 * ramo funziona identico sia per le righe scritte in convenzione canonica
 * (`type='appuntamento'`, Task 16) sia per quelle storiche in convenzione LEAD
 * (`type='alfadocs_visita'` ecc.) — nessuna delle due convenzioni influisce sul
 * write-back verso AlfaDocs, che non manda mai un `type` all'API (solo date/durata/
 * descrizione).
 */
async function syncAlfaDocsUpdate(
  input: ActivitySaveInput,
  clientId: string | undefined,
  startDateTime: Date,
  endDateTime: Date | null
): Promise<void> {
  const alfaApptId = input.editingActivity?.metadata?.alfadocs_appointment_id;
  if (!alfaApptId || !input.organizationId) return;

  try {
    const updateConfig = await resolveActivityAlfaConfig(
      input.editingActivity?.metadata?.alfadocs_practice_id || null,
      clientId,
      input.organizationId
    );
    const endTime = endDateTime || new Date(startDateTime.getTime() + 30 * 60000);
    const durationMin = Math.round((endTime.getTime() - startDateTime.getTime()) / 60000);
    const pad = (n: number) => String(n).padStart(2, '0');
    const alfaDate = `${startDateTime.getFullYear()}-${pad(startDateTime.getMonth() + 1)}-${pad(startDateTime.getDate())} ${pad(startDateTime.getHours())}:${pad(startDateTime.getMinutes())}:${pad(startDateTime.getSeconds())}`;
    await AlfaDocsService.updateAppointment(
      input.organizationId,
      alfaApptId,
      { date: alfaDate, duration: durationMin, description: input.activityData.title },
      { configId: updateConfig?.id }
    );
    // Retrocompat in LETTURA (Task 16): entrambe le convenzioni scrivono già
    // `metadata.alfadocs_type` all'insert (v. `syncAlfaDocsAppointment` e, prima del
    // Task 16, la stessa scrittura in convenzione LEAD) — lo spread preserva quel
    // valore invariato. Come rete di sicurezza per righe storiche prive del campo, si
    // deriva un fallback dal `type` legacy `alfadocs_*` GIÀ PERSISTITO sull'attività in
    // editing (mai dal `safeType` di QUESTO submit, che sul canonico sarebbe già
    // 'appuntamento' e non porterebbe più il sottotipo).
    const legacyAlfaType = resolveAlfaDocsSubType(input.editingActivity?.type);
    await supabase
      .from('sales_activities')
      .update({
        metadata: {
          ...input.editingActivity?.metadata,
          alfadocs_appointment_id: alfaApptId,
          alfadocs_type: input.editingActivity?.metadata?.alfadocs_type || legacyAlfaType || undefined,
          alfadocs_practice_id: input.editingActivity?.metadata?.alfadocs_practice_id || updateConfig?.configuration?.practice_id,
        },
      })
      .eq('id', input.editingActivity!.id);
  } catch (alfaErr) {
    console.warn('[useActivitySave] AlfaDocs update failed:', alfaErr);
  }
}
